import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { PublicLayout } from '@/components/PublicLayout';
import { fetchPublicNews } from '@/utils/api';
import { Calendar, Tag, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const News = () => {
  const { t, language } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const data = await fetchPublicNews();
      setNews(data);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    return t.news.categories[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      event: 'bg-purple-100 text-purple-700',
      news: 'bg-blue-100 text-blue-700',
      announcement: 'bg-green-100 text-green-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  // Helper to get correct image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    // If it starts with /, it's a local path - prepend backend URL
    if (imageUrl.startsWith('/')) {
      return `${BACKEND_URL}${imageUrl}`;
    }
    return imageUrl;
  };

  return (
    <PublicLayout>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6">
              {t.news.title}
            </h1>
            <p className="text-xl text-slate-600">{t.news.subtitle}</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : news.length === 0 ? (
            <div data-testid="no-news" className="max-w-4xl mx-auto">
              {/* Empty State Hero */}
              <div className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-3xl p-8 md:p-12 text-center mb-12">
                <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6">
                  <Tag className="text-[#127abb]" size={48} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  {language === 'fr' 
                    ? 'Actualités à venir' 
                    : 'News coming soon'}
                </h2>
                <p className="text-slate-600 text-lg mb-8 max-w-xl mx-auto">
                  {language === 'fr'
                    ? 'Nous préparons de nouveaux contenus pour vous tenir informés de nos activités et événements. Restez connectés !'
                    : 'We are preparing new content to keep you informed about our activities and events. Stay tuned!'}
                </p>
                <Link to="/contact">
                  <button className="bg-[#127abb] hover:bg-[#0e6292] text-white font-semibold px-8 py-4 rounded-full transition-all hover:scale-105 shadow-lg">
                    {language === 'fr' ? 'Restez informé' : 'Stay informed'}
                  </button>
                </Link>
              </div>

              {/* Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="text-[#127abb]" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Événements' : 'Events'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {language === 'fr'
                      ? 'Soirées d\'intégration, ateliers culturels et rencontres entre étudiants'
                      : 'Integration parties, cultural workshops and student meetings'}
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="text-green-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Vie associative' : 'Association life'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {language === 'fr'
                      ? 'Découvrez les coulisses de notre association et nos actions'
                      : 'Discover the behind the scenes of our association and our actions'}
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Tag className="text-amber-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Annonces' : 'Announcements'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {language === 'fr'
                      ? 'Informations importantes et nouveautés concernant nos services'
                      : 'Important information and news about our services'}
                  </p>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-gradient-to-r from-[#127abb] to-[#0e6292] rounded-3xl p-8 md:p-10 text-center">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                  {language === 'fr' 
                    ? 'Ne manquez aucune actualité' 
                    : "Don't miss any news"}
                </h3>
                <p className="text-blue-100 mb-6 max-w-lg mx-auto">
                  {language === 'fr'
                    ? 'Contactez-nous pour être informé de nos prochains événements et actualités.'
                    : 'Contact us to be informed about our upcoming events and news.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <button className="bg-white text-[#127abb] hover:bg-blue-50 font-semibold px-6 py-3 rounded-full transition-all">
                      {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                    </button>
                  </Link>
                  <Link to="/services">
                    <button className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-full transition-all border border-white/30">
                      {language === 'fr' ? 'Découvrir nos services' : 'Discover our services'}
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.map((item) => (
                <div
                  key={item.news_id}
                  data-testid={`news-card-${item.news_id}`}
                  className="bg-white border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 rounded-2xl overflow-hidden"
                >
                  <div className="h-48 overflow-hidden bg-slate-100">
                    {item.image_url ? (
                      <img
                        src={getImageUrl(item.image_url)}
                        alt={language === 'fr' ? item.title_fr : item.title_en}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center ${item.image_url ? 'hidden' : ''}`}>
                      <ImageIcon className="text-slate-300" size={48} />
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className={`text-xs font-medium px-3 py-1 rounded-full ${getCategoryColor(
                          item.category
                        )}`}
                      >
                        <Tag size={12} className="inline mr-1" />
                        {getCategoryLabel(item.category)}
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Calendar size={12} />
                        {format(new Date(item.created_at), 'dd/MM/yyyy')}
                      </span>
                    </div>

                    <h3 className="text-xl font-semibold text-slate-900 mb-3">
                      {language === 'fr' ? item.title_fr : item.title_en}
                    </h3>

                    <p className="text-slate-600 line-clamp-3">
                      {language === 'fr' ? item.content_fr : item.content_en}
                    </p>

                    <Link to={`/news/${item.news_id}`}>
                      <button className="w-full mt-4 py-2 px-4 bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full font-medium transition-colors">
                        {language === 'fr' ? 'Lire la suite' : 'Read more'}
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default News;